import {
	getPropertySortText,
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	MultiSelectOption
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { useUserTimezone, useValueEqualMemo } from "client/Hooks";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	EmailDateFilterKind,
	EmailFilterSelector,
	EmailSortDirection,
	EmailSortOrder,
	IFolderViewModel,
	LazyLoadedChildren,
	listFolders
} from "client/Api/Email/email";
import {
	EmailGlobalSearchResultItem,
	ExtendedEmailSearchResultsModel,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchEmail,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { EmailItemView } from "../Items/EmailItemView";

export type EmailSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const EmailSearch = (props: EmailSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [folderOptions, setFolderOptions] = useState<readonly SelectOption<string | undefined>[] | null>(null);
	const [loadingState, setLoadingState] = useState<ResultsLoadingState>({
		status: "loading",
		items: [],
		hasMore: true
	});

	const [formData, setFormData] = useState<EmailSearchFormData>({
		...defaultFilters,
		sorting: sortOptions[0].value
	});

	const {
		sorting: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const requestCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const listHandle: RefObject<ScrollableVirtualListHandle<EmailGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<EmailGlobalSearchResultItem>>(null);
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<EmailSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const baseRequestParameters = useValueEqualMemo(
		(): EmailSearchFormData & { searchTerm: VersionedValue<string>; } => ({
			...formData,
			// We Include version to trigger search on Enter
			searchTerm
		}),
		[formData, searchTerm]
	);

	const loadItems = useCallback(
		async (skip: number): Promise<void> => {
			requestCancellationRef.current
				?.abort();

			const {
				searchTerm,
				filterSelectors,
				folderId,
				sorting,
				timeScope
			} = baseRequestParameters;

			if (
				searchTerm.value
					.trim() === ""
			) {
				if (skip !== 0) {
					throw new Error("Skipping while not having a search term is not supported");
				}

				setLoadingState({
					status: "ready",
					items: [],
					hasMore: false
				});

				return;
			}

			setLoadingState(
				(x) => {
					if (skip === 0) {
						return {
							status: "loading",
							items: [],
							hasMore: true
						};
					} else {
						return {
							...x,
							status: "loading",
							hasMore: false
						};
					}
				}
			);

			const cancellation = new AbortController();
			requestCancellationRef.current = cancellation;

			try {
				const result: ExtendedEmailSearchResultsModel = await searchEmail(
					{
						skip,
						take: PAGE_SIZE,
						filterFolderId: isNotNil(folderId)
							? { isSet: true, value: { id: folderId } }
							: { isSet: false },
						sortOrder: sorting.property,
						sortDirection: sorting.direction,
						searchTerm: searchTerm.value,
						filterSelectors,
						dateFilterKind: timeScope
					},
					cancellation.signal
				);

				setLoadingState(
					(x) => ({
						...x,
						status: "ready",
						items: [...x.items, ...result.results.items],
						hasMore: result.results
							.hasMore
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				setLoadingState(
					(x) => ({
						...x,
						status: skip === 0 ? "error" : "ready",
						hasMore: false
					})
				);
			}
		},
		[baseRequestParameters]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			if (loadingState.status !== "ready" || !loadingState.hasMore) {
				return;
			}

			loadItems(
				loadingState.items
					.length
			);
		},
		[
			loadItems,
			loadingState.hasMore,
			loadingState.items
				.length,
			loadingState.status
		]
	);

	const reloadHandler = useCallback(
		(): void => {
			loadItems(0);
		},
		[loadItems]
	);

	useEffect(
		(): void => {
			listHandle.current
				?.scrollToTop();

			loadItems(0);
		},
		[loadItems]
	);

	useEffect(
		(): (() => void) => {
			return (): void => {
				requestCancellationRef.current
					?.abort();
			};
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			const loadFolders = async (signal: AbortSignal): Promise<void> => {
				try {
					const folders: readonly IFolderViewModel<LazyLoadedChildren>[] = await listFolders(
						{
							smtpAddress: { isSet: false },
							folderId: { isSet: false }
						},
						signal
					);

					const options: readonly SelectOption<string>[] = folders
						.map((x): SelectOption<string> => ({
							text: x.name,
							value: x.folderId
								.id
						}));

					setFolderOptions(
						[defaultFolderOption, ...options]
					);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					setFolderOptions([]);
				}
			};

			const cancellation = new AbortController();
			loadFolders(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.status === "ready" && loadingState.items.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState.status === "loading" || loadingState.items.length > 0) {
			return (
				<ScrollableVirtualList
					items={loadingState.items}
					estimatedHeightOfAnItem={113}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="email-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState.status === "loading"}
								hasMoreItems={loadingState.status !== "loading" && loadingState.hasMore}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: EmailGlobalSearchResultItem, index: number): ReactElement => {
						const emailId: string = item.item
							.emailId
							.id;

						return (
							<EmailItemView
								key={emailId}
								navigationIndex={index}
								item={item}
								itemScope={emailSearchScope}
								timezone={userTimezone}
							/>
						);
					}}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reloadHandler}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sorting")}
						options={sortOptions}
						isExpandedByDefault
					/>

					<SearchSettings.Select
						label={localize("_Folder")}
						attachable={formHandler.for("folderId")}
						options={folderOptions ?? [defaultFolderOption]}
						hasBusyIndicator={isNil(folderOptions)}
					/>

					<SearchSettings.Select
						label={localize("_Date")}
						attachable={formHandler.for("timeScope")}
						options={timeScopeOptions}
					/>

					<SearchSettings.MultiSelect
						label={localize("_FilterBy")}
						attachable={formHandler.for("filterSelectors")}
						options={filterOptions}
						placeholder={localize("_SelectFilter(s)")}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
EmailSearch.displayName = "EmailSearch";

function getUniqueKey(item: EmailGlobalSearchResultItem): string {
	return item.item
		.emailId
		.id;
}

export const PAGE_SIZE = 20;

type Sortvalue = {
	direction: Exclude<EmailSortDirection, EmailSortDirection.Default>;
	property: Exclude<EmailSortOrder, EmailSortOrder.None>;
};

type EmailSearchFormData = {
	sorting: Sortvalue;
	folderId: string | undefined;
	timeScope: EmailDateFilterKind;
	filterSelectors: readonly EmailFilterSelector[];
};

const defaultFolderOption: SelectOption<string | undefined> = {
	value: undefined,
	text: localize("_AllFolders")
};

type FilterModel = Omit<EmailSearchFormData, "sorting">;

const defaultFilters: Omit<EmailSearchFormData, "sorting"> = {
	folderId: defaultFolderOption.value,
	timeScope: EmailDateFilterKind.Any,
	filterSelectors: []
};

const sortOptions: readonly SelectOption<Sortvalue>[] = [
	{
		text: getPropertySortText("_Date", "_NewToOld"),
		value: { property: EmailSortOrder.Date, direction: EmailSortDirection.Descending },
		valueTrackingId: "date-new-old"
	},
	{
		text: getPropertySortText("_Date", "_OldToNew"),
		value: { property: EmailSortOrder.Date, direction: EmailSortDirection.Ascending },
		valueTrackingId: "date-old-new"
	},
	{
		text: getPropertySortText("_From", "_AToZ"),
		value: { property: EmailSortOrder.From, direction: EmailSortDirection.Ascending },
		valueTrackingId: "from-a-z"
	},
	{
		text: getPropertySortText("_From", "_ZToA"),
		value: { property: EmailSortOrder.From, direction: EmailSortDirection.Descending },
		valueTrackingId: "from-z-a"
	},
	{
		text: getPropertySortText("_Subject", "_AToZ"),
		value: { property: EmailSortOrder.Subject, direction: EmailSortDirection.Ascending },
		valueTrackingId: "subject-a-z"
	},
	{
		text: getPropertySortText("_Subject", "_ZToA"),
		value: { property: EmailSortOrder.Subject, direction: EmailSortDirection.Descending },
		valueTrackingId: "subject-z-a"
	}
];


const timeScopeOptions: readonly SelectOption<EmailDateFilterKind>[] = [
	{
		value: EmailDateFilterKind.Any,
		text: localize("_AnyTime")
	},
	{
		value: EmailDateFilterKind.Past24Hours,
		text: localize("_Past24hours")
	},
	{
		value: EmailDateFilterKind.PastWeek,
		text: localize("_PastWeek")
	},
	{
		value: EmailDateFilterKind.PastMonth,
		text: localize("_PastMonth")
	},
	{
		value: EmailDateFilterKind.Past3Months,
		text: localize("_Past3Months")
	},
	{
		value: EmailDateFilterKind.PastYear,
		text: localize("_PastYear")
	}
];

const filterOptions: readonly MultiSelectOption<EmailFilterSelector>[] = [
	{
		value: EmailFilterSelector.HasAttachment,
		text: localize("_Attachment")
	},
	{
		value: EmailFilterSelector.Flagged,
		text: localize("_Flagged")
	},
	{
		value: EmailFilterSelector.Unread,
		text: localize("_Unread")
	}
];

type ResultsLoadingState = {
	status: "loading" | "ready" | "error";
	items: readonly EmailGlobalSearchResultItem[];
	hasMore: boolean;
};

const emailSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Emails
};
